import { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import type { Notification } from './types.ts'

export interface UseNotificationsOptions {
  /** Initial notifications (page 0) */
  initialNotifications: Notification[]
  /** Called to fetch the next page. Return notifications and whether more exist. */
  fetchMore?: (page: number) => Promise<{ notifications: Notification[]; hasMore: boolean }> | { notifications: Notification[]; hasMore: boolean }
  /** Simulated delay for mock loading (ms). Default: 800 */
  loadDelay?: number
}

export interface UseNotificationsReturn {
  /** Active (non-dismissed) notifications */
  notifications: Notification[]
  /** All notifications including dismissed — for audit */
  allNotifications: Notification[]
  unreadCount: number
  isOpen: boolean
  open: () => void
  close: () => void
  toggle: () => void
  markRead: (id: string) => void
  markAllRead: () => void
  /** Soft delete — sets dismissedAt, filters from view */
  dismiss: (id: string) => void
  /** Soft delete all active notifications */
  dismissAll: () => void
  /** Soft delete all read notifications */
  clearRead: () => void
  /** Undo a dismiss (restore from soft delete) */
  undoDismiss: (id: string) => void
  /** Find a notification by ID (including dismissed) */
  getById: (id: string) => Notification | undefined
  /** Pagination state */
  hasMore: boolean
  isLoadingMore: boolean
  loadMore: () => void
}

export function useNotifications(
  initialNotifications: Notification[],
  options?: Omit<UseNotificationsOptions, 'initialNotifications'>,
): UseNotificationsReturn {
  const [allNotifications, setAllNotifications] = useState(initialNotifications)
  const [isOpen, setIsOpen] = useState(false)
  const [hasMore, setHasMore] = useState(!!options?.fetchMore)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const pageRef = useRef(1) // page 0 is initialNotifications

  // Sync when initialNotifications changes (e.g. after API fetch replaces empty array)
  useEffect(() => {
    setAllNotifications(initialNotifications)
    pageRef.current = 1
  }, [initialNotifications])

  const notifications = useMemo(
    () => allNotifications.filter((n) => !n.dismissedAt),
    [allNotifications],
  )

  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.read).length,
    [notifications],
  )

  const open = useCallback(() => setIsOpen(true), [])
  const close = useCallback(() => setIsOpen(false), [])
  const toggle = useCallback(() => setIsOpen((prev) => !prev), [])

  const markRead = useCallback((id: string) => {
    setAllNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n)),
    )
  }, [])

  const markAllRead = useCallback(() => {
    setAllNotifications((prev) =>
      prev.map((n) => (n.dismissedAt ? n : { ...n, read: true })),
    )
  }, [])

  const dismiss = useCallback((id: string) => {
    const now = new Date().toISOString()
    setAllNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, dismissedAt: now, read: true } : n)),
    )
  }, [])

  const dismissAll = useCallback(() => {
    const now = new Date().toISOString()
    setAllNotifications((prev) =>
      prev.map((n) => (n.dismissedAt ? n : { ...n, dismissedAt: now, read: true })),
    )
  }, [])

  const clearRead = useCallback(() => {
    const now = new Date().toISOString()
    setAllNotifications((prev) =>
      prev.map((n) => (n.read && !n.dismissedAt ? { ...n, dismissedAt: now } : n)),
    )
  }, [])

  const undoDismiss = useCallback((id: string) => {
    setAllNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, dismissedAt: null } : n)),
    )
  }, [])

  const getById = useCallback(
    (id: string) => allNotifications.find((n) => n.id === id),
    [allNotifications],
  )

  const loadMore = useCallback(() => {
    if (!options?.fetchMore || isLoadingMore || !hasMore) return
    const page = pageRef.current
    setIsLoadingMore(true)

    const delay = options.loadDelay ?? 800

    const doFetch = async () => {
      // Simulate network delay for mocks
      if (delay > 0) {
        await new Promise((r) => setTimeout(r, delay))
      }
      const result = await options.fetchMore!(page)
      setAllNotifications((prev) => [...prev, ...result.notifications])
      setHasMore(result.hasMore)
      pageRef.current = page + 1
      setIsLoadingMore(false)
    }

    doFetch()
  }, [options, isLoadingMore, hasMore])

  return {
    notifications,
    allNotifications,
    unreadCount,
    isOpen,
    open,
    close,
    toggle,
    markRead,
    markAllRead,
    dismiss,
    dismissAll,
    clearRead,
    undoDismiss,
    getById,
    hasMore,
    isLoadingMore,
    loadMore,
  }
}
